import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { logAuthEvent, resetNavigationAudit } from '../utils/sessionAudit';

const AuthContext = createContext({});
// `SIGNED_IN` tambien se emite al recargar la pagina y al refrescar el token.
// Sin este guard, la bitacora contaria un "login" por cada F5 y el dato dejaria
// de servir para saber quien entro y cuando.
const LOGIN_AUDIT_KEY = 'impulza_login_audited_user';

const yaSeAuditoElLogin = (userId) => {
  if (!userId || typeof window === 'undefined') return true;
  try {
    return window.sessionStorage.getItem(LOGIN_AUDIT_KEY) === userId;
  } catch {
    return false;
  }
};

const marcarLoginAuditado = (userId) => {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(LOGIN_AUDIT_KEY, userId);
  } catch {
    // ignore
  }
};

const olvidarLoginAuditado = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(LOGIN_AUDIT_KEY);
  } catch {
    // ignore
  }
};

const PROFILE_REFRESH_EVENTS = new Set(['INITIAL_SESSION', 'USER_UPDATED']);

const fetchUserRecord = async (userId) => {
  for (const tableName of ['profiles', 'users']) {
    try {
      const response = await supabase
        .from(tableName)
        .select('*')
        .eq('id', userId)
        .single();

      if (response?.data) return response.data;
    } catch (error) {
      console.warn(`[AuthContext] No se pudo cargar ${tableName}.`, error);
    }
  }

  return null;
};

const buildProfileFromAuthUser = (authUser) => {
  if (!authUser?.id) return null;

  const metadata = authUser.user_metadata || {};

  return {
    id: authUser.id,
    user_id: authUser.id,
    email: authUser.email || metadata.email || '',
    full_name: metadata.full_name || metadata.name || authUser.email || '',
    organization_id: metadata.organization_id || null,
    role: metadata.role || 'admin',
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const profileRef = useRef(null);

  useEffect(() => {
    profileRef.current = profile;
    // Hookear con Sentry si está disponible — para que cada error reportado
    // tenga email/org_id del usuario afectado (ver main.jsx).
    if (typeof window !== 'undefined' && typeof window.__gastifyTagUser === 'function') {
      window.__gastifyTagUser(
        profile
          ? { id: profile.id, email: profile.email, organization_id: profile.organization_id }
          : null,
      );
    }
  }, [profile]);

  // Función para traer los datos de la organización
  const fetchOrganization = async (orgId) => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', orgId)
        .single();
      if (!error && data) {
        setOrganization(data);
      } else {
        setOrganization(null);
      }
    } catch (error) {
      console.warn('[AuthContext] No se pudo cargar la organización.', error);
      setOrganization(null);
    }
  };

  const refreshOrganization = async () => {
    const orgId = profile?.organization_id || profileRef.current?.organization_id;
    if (orgId) {
      await fetchOrganization(orgId);
    }
  };

  useEffect(() => {
    // 1. Verificar sesión actual
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser((current) => {
        const nextUser = session?.user ?? null;
        if (current?.id === nextUser?.id) return current;
        return nextUser;
      });
      if (session?.user) fetchProfile(session.user.id, session.user);
      else setLoading(false);
    });

    // 2. Escuchar cambios en el auth (login/logout)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser((current) => {
        const nextUser = session?.user ?? null;
        if (current?.id === nextUser?.id) return current;
        return nextUser;
      });
      if (!session?.user) {
        // Se registra ANTES de limpiar: despues ya no sabemos quien se fue.
        const salienteId = profileRef.current?.id || null;
        if (salienteId) {
          logAuthEvent('logout', { user: { id: salienteId }, profile: profileRef.current });
        }
        olvidarLoginAuditado();
        resetNavigationAudit();
        setProfile(null);
        setOrganization(null);
        setLoading(false);
        return;
      }

      if (event === 'SIGNED_IN') {
        if (!yaSeAuditoElLogin(session.user.id)) {
          marcarLoginAuditado(session.user.id);
          logAuthEvent('login', { user: session.user, profile: profileRef.current });
        }
        const currentProfileId = profileRef.current?.id || null;
        if (!currentProfileId || currentProfileId !== session.user.id) {
          fetchProfile(session.user.id, session.user);
          return;
        }

        setLoading(false);
        return;
      }

      if (PROFILE_REFRESH_EVENTS.has(event)) {
        fetchProfile(session.user.id, session.user);
        return;
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Función para traer el organization_id y el rol desde nuestra tabla 'profiles'
  const fetchProfile = async (userId, authUserOverride = null) => {
    let data = await fetchUserRecord(userId);

    if (!data) {
      const authUser = authUserOverride || user || null;
      data = buildProfileFromAuthUser(authUser);
    }
    setProfile(data);

    if (data?.organization_id) {
      await fetchOrganization(data.organization_id);
    } else {
      setOrganization(null);
    }
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, profile, organization, refreshOrganization, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
